import { useCallback, useRef, useState } from "react";

import { refusalText } from "../../../api/errors";
import type { ModelList, TestResultWord } from "../../../api/types";

/**
 * Where the choice of a model stands. No model name is preset anywhere: the person types the key, presses Connect, and the
 * service says which models it offers.
 *
 * - `idle`: nothing was asked yet (or the kind, the address or the key changed since, so the last answer is stale);
 * - `loading`: the service is being asked;
 * - `listed`: the service gave a list: pick one;
 * - `no_list`: the service answered but has no list (some OpenAI-compatible servers do not): type the model name;
 * - `failed`: one of the plain results of Test connection (key refused, address not reached, something else);
 * - `refused`: the request itself was refused (an address that is not allowed, no key typed, and so on).
 */
export type ModelListState =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "listed"; models: string[] }
  | { phase: "no_list"; message: string }
  | { phase: "failed"; result: Exclude<TestResultWord, "connected">; message: string }
  | { phase: "refused"; message: string };

export function stateOfAnswer(answer: ModelList): ModelListState {
  if (answer.result === "connected") {
    return answer.listed ? { phase: "listed", models: answer.models } : { phase: "no_list", message: answer.message };
  }
  return { phase: "failed", result: answer.result, message: answer.message };
}

/**
 * The state of one Connect / Change model button. `ask` runs the command and keeps the answer; `reset` forgets it (the kind,
 * the address or the key changed). An answer that arrives after a reset is dropped. The key is never kept here.
 */
export function useModelList() {
  const [state, setState] = useState<ModelListState>({ phase: "idle" });
  const turn = useRef(0);

  const ask = useCallback(async (run: () => Promise<{ data: ModelList }>): Promise<ModelListState | null> => {
    const mine = ++turn.current;
    setState({ phase: "loading" });
    let next: ModelListState;
    try {
      next = stateOfAnswer((await run()).data);
    } catch (error) {
      next = { phase: "refused", message: refusalText(error) };
    }
    if (mine !== turn.current) return null;
    setState(next);
    return next;
  }, []);

  const reset = useCallback(() => {
    turn.current += 1;
    setState((now) => (now.phase === "idle" ? now : { phase: "idle" }));
  }, []);

  return { state, ask, reset };
}
